/**
 * Respaldo local del texto de las notas de un aseo (round 2026-10-26).
 *
 * El texto se autoguarda en el servidor, pero en terreno la señal se corta: si
 * el guardado falla (o la aseadora cierra la app antes de que llegue), el
 * texto se perdería. Cada cambio se respalda aquí en localStorage y el respaldo
 * se borra SOLO cuando el servidor confirma que lo guardó. Al reabrir las
 * notas, un respaldo que sigue ahí es texto que nunca llegó: se recupera.
 *
 * localStorage puede fallar o venir vacío (modo privado, sitio bloqueado,
 * cuota): todo va en try/catch y la pantalla funciona igual sin respaldo.
 * La clave incluye el usuario para que en un celular compartido una aseadora
 * no vea el borrador de otra.
 */

const PREFIJO = "aseo.nota.borrador.v1";
export const TTL_BORRADOR_MS = 7 * 24 * 60 * 60 * 1000;

interface Borrador {
  texto: string;
  ts: number;
}

export const claveBorrador = (
  usuarioId: number | string | null | undefined,
  aseoId: number,
  tipo: string,
) => `${PREFIJO}:${usuarioId ?? "anon"}:${aseoId}:${tipo}`;

export function guardarBorrador(clave: string, texto: string, ahora = Date.now()): void {
  try {
    const b: Borrador = { texto, ts: ahora };
    localStorage.setItem(clave, JSON.stringify(b));
  } catch {
    /* sin respaldo local: el autoguardado del servidor sigue funcionando */
  }
}

/** Devuelve el respaldo vigente, o null (inexistente, corrupto o vencido). */
export function leerBorrador(clave: string, ahora = Date.now()): Borrador | null {
  try {
    const raw = localStorage.getItem(clave);
    if (!raw) return null;
    const b = JSON.parse(raw) as Partial<Borrador>;
    if (typeof b?.texto !== "string" || typeof b?.ts !== "number") {
      localStorage.removeItem(clave);
      return null;
    }
    if (ahora - b.ts > TTL_BORRADOR_MS) {
      localStorage.removeItem(clave);
      return null;
    }
    return { texto: b.texto, ts: b.ts };
  } catch {
    return null;
  }
}

/**
 * Borra el respaldo. Con `textoConfirmado` solo lo borra si coincide con el que
 * el servidor acaba de aceptar: así un guardado que tarda no borra lo que la
 * aseadora siguió escribiendo mientras tanto.
 */
export function limpiarBorrador(clave: string, textoConfirmado?: string): void {
  try {
    if (textoConfirmado !== undefined) {
      const actual = leerBorrador(clave);
      if (actual && actual.texto !== textoConfirmado) return;
    }
    localStorage.removeItem(clave);
  } catch {
    /* nada */
  }
}

/** Pausas (ms) entre reintentos del autoguardado: 3, 8, 15, 30 s y luego cada 60 s. */
export function esperaReintentoTexto(fallos: number): number {
  const tabla = [3000, 8000, 15000, 30000];
  return fallos <= 0 ? tabla[0] : (tabla[fallos - 1] ?? 60000);
}
